import React, { FunctionComponent } from 'react';
import i18next from 'core/i18n';
import {
  Table,
  palettes,
  ColumnDefinition,
  RegularTitle,
  RowDefinition,
  Card,
  SmallTitle,
  PageInfo,
  TagsFooterProps
} from '@indigo-ag/component-module';
import {
  AgFinancing,
  FinancingBrazilGrowerLead,
  FinancingState,
  AgOpportunity,
  AgMessage,
  AgConversation,
  AgFinancingFile
} from '@indigo-ag/schema';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faMinusCircle, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { faFileAlt } from '@fortawesome/pro-light-svg-icons';
import orderBy from 'lodash/orderBy';

import { createStylesHook, CURRENCY_SYMBOL, SortType } from 'shared';
import { text, currency, date, UserUtil } from 'shared/utils';
import { calculateStages } from '../../shared-br-grower';

export interface Notification {
  id: AgConversation['id'];
  financingId: AgFinancing['id'];
  name?: FinancingBrazilGrowerLead['name'];
  creditAnalystAssigned?: AgFinancing['creditAnalystAssigned'];
  state?: FinancingState['name'];
  guarantyType: AgOpportunity['collateralType'];
  contractValue: AgOpportunity['contractValue'];
  previewMessage?: AgMessage;
  phase2: AgFinancing['phase2'];
}

interface NotificationsListProps {
  onTableAction?: (action: any) => void;
  pagination?: PageInfo;
  notifications?: Notification[];
}

const useStyles = createStylesHook(({ typography: { pxToRem } }) => ({
  lastMessage: {
    display: 'flex',
    padding: pxToRem(24),
    justifyContent: 'space-between',
    background: `${palettes.brand.blue.lighter} !important`,
    color: palettes.brand.gray['800'],
    width: '100%'
  },
  attachmentPreview: {
    display: 'flex',
    '& > svg': {
      marginTop: '0.2rem',
      marginRight: pxToRem(10)
    }
  },
  sendAt: {
    fontSize: pxToRem(13),
    color: palettes.brand.gray['700'],
    marginBottom: '4px'
  },
  sentBy: {
    'font-weight': 'normal !important',
    marginBottom: '8px !important'
  },
  actions: {
    height: 'auto',
    width: '16px',
    position: 'relative',
    padding: '10px'
  },
  actionIcon: {
    position: 'absolute',
    top: '50%',
    marginTop: '-10px',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.3)',
      transition: '.3s',
      color: palettes.brand.gray['700']
    }
  },
  messageInfo: {
    maxHeight: '120px',
    overflowY: 'auto'
  },
  // TODO: FFigure out an alternative to do this better
  globalStyles: {
    '& div[role="cell"]:nth-child(4)': {
      marginTop: '10px',
      marginBottom: '-46px',
      marginRight: '-85px',
      paddingLeft: '0',
      maxWidth: '50vw',
      width: '100%'
    }
  }
}));

//Table Stuff
type ColumnId = 'name' | 'analyst' | 'state' | 'guarantyType' | 'message';

const columnDefinition = (classes: any): ColumnDefinition<Notification, string>[] => [
  {
    id: 'name',
    header: i18next.t('name'),
    cell: ({ row }) => (
      <Link to={`/br-growers/${row.financingId}`}>
        <RegularTitle style={{ fontWeight: 400, color: 'inherit' }}>{text.toTitleCase(row.name || '')}</RegularTitle>
      </Link>
    )
  },
  {
    id: 'analyst',
    header: text.toTitleCase(i18next.t('creditAnalyst')),
    cell: ({ row, SimpleCell }) => (
      <SimpleCell label={row.creditAnalystAssigned ? UserUtil.getUserFullName(row.creditAnalystAssigned) : '-'} />
    )
  },
  {
    id: 'state',
    header: i18next.t('state'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.state || '-'} />
  },
  {
    id: 'guarantyType',
    header: i18next.t('guarantyType'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.guarantyType || '-'} />
  },
  {
    id: 'message',
    header: '',
    cell: ({ row: { financingId, id, previewMessage } }) => {
      const lastAttachment: AgFinancingFile | undefined = [
        ...orderBy(previewMessage?.attachments, SortType.DESC)
      ].shift();
      return (
        <Card className={classes.lastMessage}>
          <div className={classes.messageInfo}>
            <span className={classes.sendAt}>
              {date.messageSentAtFormat(
                previewMessage?.sentAt || new Date(),
                date.todayAt00(),
                undefined,
                'hh:mm - MMM DD, YYYY'
              )}
            </span>
            <SmallTitle className={classes.sentBy}>
              {`${UserUtil.getUserFullName(previewMessage?.sentBy)} - ${i18next.t(
                previewMessage?.sentBy?.role?.id || ''
              )}`}
            </SmallTitle>
            {previewMessage?.attachments?.length ? (
              <span className={classes.attachmentPreview}>
                <FontAwesomeIcon icon={faFileAlt} color={palettes.neutral[700]} />
                {lastAttachment?.documentName}
              </span>
            ) : (
              previewMessage?.body
            )}
          </div>
          <Link to={`/br-growers/${financingId}/conversations/all/${id}`} className={classes.actions}>
            <FontAwesomeIcon
              size="lg"
              className={classes.actionIcon}
              icon={faAngleRight}
              color={palettes.brand.blue.base}
              fontWeight="300"
            />
          </Link>
        </Card>
      );
    }
  }
];

const rowDefinition: RowDefinition<Notification, ColumnId> = {
  rowId: ({ row }) => row.id,
  footer: ({ row: { id, contractValue, phase2 }, TagsFooter }) => {
    const tags: TagsFooterProps['tags'] = [];
    if (contractValue) {
      tags.push({
        id: 'amount',
        label: currency.inputMaskCurrency(contractValue, {
          useComaForDecimals: true,
          currencySymbol: CURRENCY_SYMBOL.BR
        })
      });
    }
    Object.entries(calculateStages(phase2))
      .filter(([stage]) => !!stage)
      .map(([stage, completed], index) =>
        tags.push({
          id: `${index}-${stage}`,
          label: stage,
          icon: completed ? faCheckCircle : faMinusCircle,
          iconColor: palettes.legacy[completed ? 'green' : 'gray'].default
        })
      );
    return <TagsFooter tags={tags} />;
  }
};

export const NotificationsList: FunctionComponent<NotificationsListProps> = ({
  notifications,
  onTableAction,
  pagination
}) => {
  /**
   * styles
   */
  const classes = useStyles();

  return (
    <div className={classes.globalStyles}>
      <Table
        label="Notification"
        data={notifications || []}
        columnDefinition={columnDefinition(classes)}
        rowDefinition={rowDefinition}
        paginationStyle="prev-next"
        onTableAction={onTableAction}
        pagination={pagination}
      />
    </div>
  );
};
